import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { StudentView, StudentViewOff } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { expr } from '@codegrade/utils';
import * as models from '@/models';
import * as permissions from '@/permissions';
import { CourseRolesStore, CoursesStore, UserStore } from '@/store';
import * as utils from '@/utils';
import { SidebarButton } from './SidebarButton';
export default tsx.component({
    name: 'toggle-student-view-button',
    props: {
        loggedInUser: p.ofType().required,
    },
    /** @returns The data for this component. */
    data() {
        return { disabling: false };
    },
    computed: {
        /** @returns Whether we are currently in student view. */
        isCurrentlyInStudentView() {
            return this.loggedInUser.getStudentViewState().tag === 'enabled';
        },
        /** @returns The message to show on the toggle button. */
        popoverMessage() {
            return this.isCurrentlyInStudentView ? 'Leave student view' : 'Enable student view';
        },
        /** @returns The loading alert for screen readers on the submitbutton action. */
        loadingAlert() {
            return this.disabling
                ? 'You are leaving the student view'
                : 'You are enabling the student view';
        },
        /** @returns The success alert for screen readers on the submitbutton action. */
        successAlert() {
            return this.disabling
                ? 'You have left the student view'
                : 'You have enabled student view';
        },
        /** @returns The enable/disable action for the toggle button. */
        submitButtonAction() {
            if (this.disabling) {
                return Maybe.of(this.disableStudentView);
            }
            const studentViewState = this.loggedInUser.getStudentViewState();
            return expr.match(studentViewState, 'tag', {
                disabled: () => this.enableStudentViewFunction,
                enabled: ({ canLeave }) => {
                    if (!canLeave) {
                        return Maybe.empty();
                    }
                    return Maybe.of(this.disableStudentView);
                },
            });
        },
        /** @returns The student view page to go to after enabling. */
        studentViewPage() {
            const meta = this.$route.meta;
            if (meta == null) {
                return Maybe.empty();
            }
            const studentView = meta.getStudentViewPage(this.$route).extract();
            if (studentView == null) {
                return Maybe.empty();
            }
            const course = CoursesStore.getCourse()(studentView.courseId).extract();
            if (course == null || !(course instanceof models.Course)) {
                return Maybe.empty();
            }
            const isUserPermitted = [
                permissions.CoursePermissions.can_edit_course_roles,
                permissions.CoursePermissions.can_see_course_roles,
            ].some(perm => course.hasPermission(perm));
            if (isUserPermitted === false) {
                return Maybe.empty();
            }
            return Maybe.of(studentView);
        },
        /**
         * Get a function to enable student view.
         *
         * We do so by removing the set of permissions for the course that the
         * teacher should not have.
         *
         * Note that we DO NOT restrict the 'can_see_hidden_assignments' permission as we have to
         * support the teacher to inspect their assignment before they are available for the
         * students.
         *
         * @returns Maybe, if we can on the current route, a function to enable student view.
         */
        enableStudentViewFunction() {
            return this.studentViewPage.map(studentView => 
            /** @returns A submit button result. */
            async () => {
                await CourseRolesStore.loadStudentRole({ courseId: studentView.courseId });
                const result = await CourseRolesStore.getStudentRole()(studentView.courseId)
                    .join()
                    .mapPromise(async ({ perms: studentPermMap }) => {
                    await UserStore.addRestrictions({
                        restrictions: {
                            removedPermissions: {
                                course: new Set(permissions.allCoursePermissions.filter(perm => perm !== 'can_see_course_roles' &&
                                    perm !== 'can_see_hidden_assignments' &&
                                    !studentPermMap[perm])),
                            },
                            forContext: {
                                courseId: studentView.courseId,
                            },
                        },
                    });
                    this.$router.push(studentView.location);
                });
                return result.throwOnEmpty(new Error('No role with the name "Student" was found.'));
            });
        },
    },
    methods: {
        /** @returns A function to disable student view. */
        async disableStudentView() {
            this.disabling = true;
            await UserStore.popRestrictions();
            return {
                onAfterSuccess: async () => {
                    this.disabling = false;
                },
            };
        },
    },
    /** @returns The rendered component. */
    render() {
        return utils.ifJustOrEmpty(this.submitButtonAction, submit => (<SidebarButton class="toggle-student-view-btn" icon={this.isCurrentlyInStudentView ? StudentViewOff : StudentView} label={this.popoverMessage} type={{
                tag: 'async-action',
                loadingMessage: this.loadingAlert,
                successMessage: this.successAlert,
                action: () => submit().finally(() => {
                    this.disabling = false;
                }),
            }}/>));
    },
});
